import { TOKEN_STORAGE_KEY } from './config';
import { cancelExpiry, isTokenExpired, scheduleExpiry } from './sessionExpiry';

/**
 * Returns the current session JWT from sessionStorage, or null if the user
 * is not signed in. Same call-site shape as the previous MSAL-backed hook
 * so apiFetch wiring doesn't change.
 *
 * The token is written by the LoginPage on a successful login response and
 * cleared on logout. It survives page reloads but not tab close — by design;
 * we want a fresh sign-in per browser session.
 *
 * If the stored token's `exp` is already in the past we return null rather
 * than handing back stale credentials — the next render then drops to the
 * /login route via RequireAuth.
 */
export function useAccessToken() {
  return async (): Promise<string | null> => {
    if (typeof window === 'undefined') return null;
    const token = window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) return null;
    if (isTokenExpired(token)) return null;
    return token;
  };
}

/**
 * Synchronous read of the current session token (or null) — same
 * missing/expired handling as the hook above, minus the `Promise` wrapper.
 *
 * Exists for call sites that need the raw token value in the SAME
 * synchronous tick as a user gesture, e.g. the console→analyser auth
 * handoff (`stashConsoleAuthToken`) immediately before `window.open`: an
 * `await getToken()` there would push `window.open` past the click's
 * synchronous call stack, and some browsers (Safari in particular) only
 * honour "opened by a real user gesture" — the thing that keeps a popup
 * from being silently blocked — for calls still on that original stack.
 */
export function getAccessTokenSync(): string | null {
  if (typeof window === 'undefined') return null;
  const token = window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) return null;
  if (isTokenExpired(token)) return null;
  return token;
}

/** Writes the token to sessionStorage (called by LoginPage after a successful login). */
export function setSessionToken(token: string): void {
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem(TOKEN_STORAGE_KEY, token);
    scheduleExpiry(token);
  }
}

/** Clears the session token (called by Logout). */
export function clearSessionToken(): void {
  if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    cancelExpiry();
  }
}
